import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import EmailComposer from '../components/EmailComposer';
import EmailReadingPane from '../components/EmailReadingPane';
import TemplateManager from '../components/TemplateManager';
import WorkflowVisualizer from '../components/WorkflowVisualizer';
import EmailSettings from '../components/EmailSettings';
import { useNotify } from '../components/NotifyProvider';
import LinkToItemModal from '../components/LinkToItemModal';
import { fetchInboundEmailById } from '../lib/supabaseService';
import {
  Mail,
  Search,
  Settings,
  Inbox,
  Send,
  Clock,
  FileText,
  ChevronLeft,
  RefreshCw,
  Zap,
} from 'lucide-react';

/**
 * Email Page — Gmail-style inbox experience
 * Two-panel design: email list (left) + reading pane (right)
 * On mobile: show list or pane, with back button for pane
 */
const Email = ({ onSelectEvent, onNavigate }) => {
  const {
    events = [],
    venues = [],
    emailTemplates = [],
    setEmailTemplates,
    sentEmails = [],
    addSentEmail,
    workflowsEnabled = true,
    setWorkflowsEnabled,
    upcomingReminders = [],
    eventEmailOverrides = {},
    toggleEventEmailOverride,
    // New AppContext additions (defaulted for now):
    inboundEmails = [],
    unreadEmailCount = 0,
    markEmailRead = () => {},
    linkEmailToEvent = async () => {},
    linkEmailToBooking = async () => {},
    archiveEmail = async () => {},
    refreshInboundEmails = async () => {},
    tableBookings = [],
  } = useAppContext();

  const [isRefreshing, setIsRefreshing] = useState(false);

  const { notifySuccess, notifyError } = useNotify();

  // Local state
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [activeFilter, setActiveFilter] = useState('inbox'); // 'inbox', 'sent', 'all', 'scheduled', 'templates', 'automations'
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [fullEmailBody, setFullEmailBody] = useState({});
  const loadingBodyRef = useRef(new Set());
  const [isMobileReadingPane, setIsMobileReadingPane] = useState(false);
  // Link-picker modal state — mode is 'event' or 'booking'
  const [linkModal, setLinkModal] = useState({ open: false, mode: 'event' });
  // Reply / Forward state feeds into EmailComposer when the pane's action
  // buttons are clicked.
  const [composerInitial, setComposerInitial] = useState({ to: '', subject: '', body: '' });

  // Build unified email list
  const unifiedEmails = useMemo(() => {
    const outbound = (sentEmails || []).map((e) => ({
      ...e,
      direction: 'outbound',
      id: e.id || `out_${e.sentAt}_${e.to}`,
      senderName: 'Errigal Events',
      senderEmail: 'events@errigalhotel.com',
      recipientName: e.eventName || e.to,
      recipientEmail: e.to,
      snippet: (e.body || '')
        .replace(/<[^>]+>/g, '')
        .substring(0, 100),
      date: e.sentAt,
      isRead: true,
      hasAttachments: e.attachments?.length > 0,
      linkedEventId: e.eventId,
      linkedEventName: e.eventName,
    }));

    // Scrub the literal "undefined" values that pre-bugfix Edge Function
    // runs stored for some inbound emails (see fetch-emails/index.ts).
    const clean = (v) => {
      if (!v) return '';
      const t = String(v).trim();
      if (!t || t === 'undefined' || t === 'undefined@undefined' || t === 'unknown') return '';
      return t;
    };
    const inbound = (inboundEmails || []).map((e) => {
      const rawName = clean(e.fromName) || clean(e.from_name);
      const rawAddr = clean(e.from) || clean(e.fromAddress) || clean(e.from_address);
      const rawTo = clean(e.to) || clean(e.toAddress) || clean(e.to_address);
      return ({
        ...e,
        direction: 'inbound',
        id: e.id || `in_${e.date}_${rawAddr || 'unknown'}`,
        senderName: rawName || rawAddr || '— unknown sender —',
        senderEmail: rawAddr,
        recipientEmail: rawTo || 'events@errigalhotel.com',
      snippet: (e.snippet || e.bodyText || e.body_text || '').substring(0, 100),
      date: e.date || e.received_at,
      isRead: e.is_read || false,
      hasAttachments: e.has_attachments || false,
      linkedEventId: e.event_id || e.processed_event_id,
      linkedEventName: null,
      });
    });

    return [...outbound, ...inbound].sort(
      (a, b) => new Date(b.date) - new Date(a.date)
    );
  }, [sentEmails, inboundEmails]);

  // Filter emails based on active filter and search
  const filteredEmails = useMemo(() => {
    let filtered = unifiedEmails;

    // Filter by tab
    if (activeFilter === 'inbox') {
      filtered = filtered.filter((e) => e.direction === 'inbound');
    } else if (activeFilter === 'sent') {
      filtered = filtered.filter((e) => e.direction === 'outbound');
    } else if (activeFilter === 'scheduled') {
      // Show upcoming reminders as scheduled emails
      return upcomingReminders.map((reminder) => ({
        id: `reminder_${reminder.id}`,
        direction: 'scheduled',
        senderName: 'Reminder',
        senderEmail: 'reminders@errigalhotel.com',
        recipientName: reminder.eventName,
        recipientEmail: reminder.client,
        subject: reminder.templateName,
        snippet: reminder.description,
        date: reminder.dueDate,
        isRead: true,
        hasAttachments: false,
        linkedEventId: reminder.eventId,
        linkedEventName: reminder.eventName,
      }));
    } else if (activeFilter === 'templates') {
      // Return nothing, templates shown in separate section
      return [];
    }

    // Search filtering
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (e) =>
          (e.senderName?.toLowerCase().includes(query) ||
            e.recipientName?.toLowerCase().includes(query) ||
            e.recipientEmail?.toLowerCase().includes(query) ||
            e.senderEmail?.toLowerCase().includes(query) ||
            e.subject?.toLowerCase().includes(query) ||
            e.snippet?.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [unifiedEmails, activeFilter, searchQuery, upcomingReminders]);

  const selectedEmail = useMemo(
    () => filteredEmails.find((e) => e.id === selectedEmailId),
    [filteredEmails, selectedEmailId]
  );

  // Calculate unread count for inbox tab
  const inboxUnreadCount = useMemo(
    () =>
      (inboundEmails || []).filter((e) => !e.is_read).length,
    [inboundEmails]
  );

  const handleSelectEmail = useCallback((email) => {
    setSelectedEmailId(email.id);
    // Mark as read
    if (!email.isRead && email.direction === 'inbound') {
      markEmailRead(email.id);
    }
    // Lazy-load full body for inbound emails (poll only fetches list fields)
    if (email.direction === 'inbound' && !fullEmailBody[email.id] && !loadingBodyRef.current.has(email.id)) {
      loadingBodyRef.current.add(email.id);
      fetchInboundEmailById(email.id)
        .then((full) => setFullEmailBody((prev) => ({ ...prev, [email.id]: full })))
        .catch((err) => console.warn('[Email] Failed to load body:', err))
        .finally(() => loadingBodyRef.current.delete(email.id));
    }
    // On mobile, show reading pane
    if (window.innerWidth < 1024) {
      setIsMobileReadingPane(true);
    }
  }, [markEmailRead, fullEmailBody]);

  const handleBackFromPane = useCallback(() => {
    setIsMobileReadingPane(false);
  }, []);

  // Build a quoted-reply body block that looks reasonable in most clients.
  const buildQuotedBody = useCallback((email, kind /* 'reply' | 'forward' */) => {
    const from = email?.senderEmail || email?.from || email?.from_address || '';
    const fromName = email?.senderName || email?.fromName || email?.from_name || from;
    const when = email?.date ? new Date(email.date).toLocaleString('en-IE') : '';
    const original = (email?.bodyHtml || email?.body_html || '').trim();
    const plain = (email?.body || email?.bodyText || email?.body_text || '').trim();
    const inner = original
      || (plain ? plain.split('\n').map((l) => `<div>${l.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>`).join('') : '');
    const header = kind === 'forward'
      ? `<div style="color:#6b7280;font-size:12px;">---------- Forwarded message ---------</div><div style="color:#6b7280;font-size:12px;">From: ${fromName} &lt;${from}&gt;</div><div style="color:#6b7280;font-size:12px;">Date: ${when}</div><div style="color:#6b7280;font-size:12px;">Subject: ${email?.subject || ''}</div>`
      : `<div style="color:#6b7280;font-size:12px;">On ${when}, ${fromName} &lt;${from}&gt; wrote:</div>`;
    return `<p><br></p><blockquote style="margin:0 0 0 0.8ex;border-left:2px solid #d1d5db;padding-left:1ex;color:#4b5563;">${header}${inner}</blockquote>`;
  }, []);

  const handleReply = useCallback((email) => {
    if (!email) return;
    const from = email.senderEmail || email.from || email.from_address || '';
    const subject = (email.subject || '').startsWith('Re:') ? email.subject : `Re: ${email.subject || ''}`;
    setComposerInitial({
      to: from,
      subject,
      body: buildQuotedBody(email, 'reply'),
    });
    setIsComposerOpen(true);
  }, [buildQuotedBody]);

  const handleForward = useCallback((email) => {
    if (!email) return;
    const subject = (email.subject || '').startsWith('Fwd:') ? email.subject : `Fwd: ${email.subject || ''}`;
    setComposerInitial({
      to: '',
      subject,
      body: buildQuotedBody(email, 'forward'),
    });
    setIsComposerOpen(true);
  }, [buildQuotedBody]);

  const handleArchive = useCallback(async (email) => {
    if (!email || email.direction !== 'inbound') return;
    try {
      await archiveEmail(email.id);
      notifySuccess('Email archived');
      setSelectedEmailId(null);
      setIsMobileReadingPane(false);
    } catch (err) {
      notifyError('Failed to archive: ' + (err?.message || 'unknown error'));
    }
  }, [archiveEmail, notifySuccess, notifyError]);

  // ── Link email to an existing event ──
  const handleOpenLinkToEvent = useCallback(() => {
    setLinkModal({ open: true, mode: 'event' });
  }, []);

  // ── Link email to an existing table booking ──
  const handleOpenLinkToBooking = useCallback(() => {
    setLinkModal({ open: true, mode: 'booking' });
  }, []);

  const handleLinkPick = useCallback(async (itemId) => {
    if (!selectedEmail || !itemId) return;
    try {
      if (linkModal.mode === 'event') {
        await linkEmailToEvent(selectedEmail.id, itemId);
        const evt = events.find((e) => e.id === itemId);
        notifySuccess(`Linked to event: ${evt?.name || itemId}`);
      } else {
        await linkEmailToBooking(selectedEmail.id, itemId);
        const bk = tableBookings.find((b) => b.id === itemId);
        notifySuccess(`Linked to booking: ${bk?.guestName || itemId}`);
      }
      setLinkModal({ open: false, mode: linkModal.mode });
    } catch (err) {
      notifyError('Failed to link: ' + (err?.message || 'unknown error'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmail, linkModal.mode, events, tableBookings, linkEmailToEvent, linkEmailToBooking]);

  // ── Create a new event pre-filled from the email ──
  const handleCreateEvent = useCallback((email) => {
    if (!email) return;
    const senderEmail = email.senderEmail || email.from || email.from_address || '';
    const senderName = email.senderName || email.fromName || email.from_name || senderEmail;
    onNavigate?.('events', {
      action: 'create-from-email',
      emailId: email.id,
      clientName: senderName,
      clientEmail: senderEmail,
      subject: email.subject || '',
      bodyText: email.body || email.bodyText || email.body_text || '',
    });
  }, [onNavigate]);

  // ── Manually trigger IMAP fetch + re-read inbox ──
  const handleRefreshInbox = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      const count = await refreshInboundEmails();
      notifySuccess(`Inbox refreshed — ${count} emails loaded`);
    } catch (err) {
      notifyError('Could not refresh inbox: ' + (err?.message || 'unknown error'));
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, refreshInboundEmails, notifySuccess, notifyError]);

  // ── Create a new table booking pre-filled from the email ──
  const handleCreateBooking = useCallback((email) => {
    if (!email) return;
    const senderEmail = email.senderEmail || email.from || email.from_address || '';
    const senderName = email.senderName || email.fromName || email.from_name || senderEmail;
    onNavigate?.('tables', {
      action: 'create-from-email',
      emailId: email.id,
      guestName: senderName,
      email: senderEmail,
      notes: [email.subject, email.body || email.bodyText || email.body_text || ''].filter(Boolean).join('\n\n'),
    });
  }, [onNavigate]);

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  const showTemplatesView = activeFilter === 'templates';
  const showAutomationsView = activeFilter === 'automations';

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Email Settings Slide-over */}
      <EmailSettings
        workflowsEnabled={workflowsEnabled}
        setWorkflowsEnabled={setWorkflowsEnabled}
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-3 md:py-4">
        <div className="max-w-full mx-auto">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Mail size={24} className="text-emerald-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Inbox</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefreshInbox}
                disabled={isRefreshing}
                title="Fetch any new emails from IMAP (events@errigalhotel.com)"
                className="flex items-center gap-1.5 px-3 py-2 text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium text-sm disabled:opacity-60 disabled:cursor-wait"
              >
                <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
                <span className="hidden sm:inline">{isRefreshing ? 'Refreshing…' : 'Refresh'}</span>
              </button>
              <button
                onClick={() => {
                  // Fresh compose — clear any lingering reply/forward seed
                  setComposerInitial({ to: '', subject: '', body: '' });
                  setIsComposerOpen(true);
                }}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm md:text-base"
              >
                Compose
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Settings size={20} />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3 top-3 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search emails..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
            />
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white border-b border-gray-200 px-4 md:px-6 overflow-x-auto">
        <div className="flex gap-1">
          {[
            { id: 'inbox', label: 'Inbox', icon: Inbox },
            { id: 'sent', label: 'Sent', icon: Send },
            { id: 'all', label: 'All', icon: Mail },
            { id: 'scheduled', label: 'Scheduled', icon: Clock },
            { id: 'templates', label: 'Templates', icon: FileText },
            { id: 'automations', label: 'Automations', icon: Zap },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => {
                setActiveFilter(id);
                setSelectedEmailId(null);
              }}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeFilter === id
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon size={18} />
              {label}
              {id === 'inbox' && inboxUnreadCount > 0 && (
                <span className="ml-1 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                  {inboxUnreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Templates View */}
        {showTemplatesView ? (
          <div className="w-full overflow-y-auto">
            <TemplateManager
              emailTemplates={emailTemplates}
              setEmailTemplates={setEmailTemplates}
              events={events}
              venues={venues}
            />
          </div>
        ) : showAutomationsView ? (
          <div className="w-full overflow-y-auto">
            <WorkflowVisualizer
              emailTemplates={emailTemplates}
              workflowsEnabled={workflowsEnabled}
              events={events}
              sentEmails={sentEmails}
            />
          </div>
        ) : (
          <>
            {/* Email List (hidden on mobile if reading pane is open) */}
            <div
              className={`${
                isMobileReadingPane ? 'hidden' : 'flex'
              } lg:flex lg:w-[35%] flex-col border-r border-gray-200 bg-white overflow-hidden`}
            >
              {filteredEmails.length > 0 ? (
                <div className="flex-1 overflow-y-auto">
                  {filteredEmails.map((email) => (
                    <EmailListItem
                      key={email.id}
                      email={email}
                      isSelected={selectedEmailId === email.id}
                      onSelect={() => handleSelectEmail(email)}
                      linkedEventName={email.linkedEventName}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-500 p-6">
                  <Mail size={48} className="text-gray-300 mb-3" />
                  <p className="font-medium">No emails</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {searchQuery
                      ? 'Try a different search'
                      : activeFilter === 'inbox'
                      ? 'All caught up!'
                      : 'No emails in this view'}
                  </p>
                </div>
              )}
            </div>

            {/* Reading Pane (hidden on mobile if list is shown) */}
            <div
              className={`${
                isMobileReadingPane ? 'flex' : 'hidden'
              } lg:flex lg:w-[65%] flex-col bg-white overflow-hidden`}
            >
              {selectedEmail ? (
                <>
                  {isMobileReadingPane && (
                    <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
                      <button
                        onClick={handleBackFromPane}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <ChevronLeft size={20} className="text-gray-600" />
                      </button>
                      <span className="text-sm font-medium text-gray-600">
                        Back to list
                      </span>
                    </div>
                  )}
                  <EmailReadingPane
                    email={selectedEmail ? { ...selectedEmail, ...(fullEmailBody[selectedEmail.id] || {}) } : selectedEmail}
                    events={events}
                    tableBookings={tableBookings}
                    onSelectEvent={onSelectEvent}
                    onReply={() => handleReply(selectedEmail)}
                    onForward={() => handleForward(selectedEmail)}
                    onCreateEvent={() => handleCreateEvent(selectedEmail)}
                    onCreateBooking={() => handleCreateBooking(selectedEmail)}
                    onLinkToEvent={handleOpenLinkToEvent}
                    onLinkToBooking={handleOpenLinkToBooking}
                    onArchive={() => handleArchive(selectedEmail)}
                  />
                </>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
                  <Mail size={48} className="text-gray-300 mb-3" />
                  <p className="font-medium">Select an email to read</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Link-to-item picker (events or table bookings) */}
      <LinkToItemModal
        isOpen={linkModal.open}
        mode={linkModal.mode}
        events={events}
        tableBookings={tableBookings}
        onPick={handleLinkPick}
        onClose={() => setLinkModal({ open: false, mode: linkModal.mode })}
      />

      {/* Email Composer Modal */}
      <EmailComposer
        isOpen={isComposerOpen}
        onClose={() => {
          setIsComposerOpen(false);
          setComposerInitial({ to: '', subject: '', body: '' });
        }}
        events={events}
        venues={venues}
        emailTemplates={emailTemplates}
        initialTo={composerInitial.to}
        initialSubject={composerInitial.subject}
        initialBody={composerInitial.body}
        onEmailSent={(email) => {
          if (addSentEmail) {
            addSentEmail(email);
          }
          setIsComposerOpen(false);
          setComposerInitial({ to: '', subject: '', body: '' });
        }}
      />
    </div>
  );
};

/**
 * Email List Item
 */
function EmailListItem({
  email,
  isSelected,
  onSelect,
  linkedEventName,
}) {
  // Format relative time
  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) {
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return dayNames[d.getDay()];
    }
    return d.toLocaleDateString('en-IE', {
      day: 'numeric',
      month: 'short',
    });
  };

  const direction = email.direction;
  const senderDisplay =
    direction === 'outbound'
      ? `To: ${email.recipientName}`
      : email.senderName || email.senderEmail;

  return (
    <button
      onClick={onSelect}
      className={`w-full px-4 py-3 border-b border-gray-100 text-left hover:bg-gray-50 transition-colors ${
        isSelected ? 'bg-emerald-50 border-b border-gray-200' : ''
      }`}
    >
      <div className="flex gap-3">
        {/* Unread indicator */}
        {!email.isRead && (
          <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2" />
        )}
        {email.isRead && <div className="w-2" />}

        {/* Email info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-gray-900 truncate text-sm">
              {senderDisplay}
            </span>
            {direction === 'outbound' && (
              <Send size={14} className="text-gray-400 flex-shrink-0" />
            )}
          </div>
          <p className="text-sm text-gray-900 truncate font-medium">
            {email.subject || '(no subject)'}
          </p>
          <p className="text-xs text-gray-500 truncate mt-0.5">
            {email.snippet}
          </p>

          {/* Tags */}
          <div className="flex items-center gap-2 mt-1.5">
            {linkedEventName && (
              <span className="inline-block px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                {linkedEventName}
              </span>
            )}
            {email.hasAttachments && (
              <span
                className="text-gray-400"
                title="Has attachments"
              >
                📎
              </span>
            )}
          </div>
        </div>

        {/* Time */}
        <span className="flex-shrink-0 text-xs text-gray-500 whitespace-nowrap">
          {formatTime(email.date)}
        </span>
      </div>
    </button>
  );
}

export default Email;
